// Umlagerung zwischen Standorten über den Transit – zwei Belegarten:
//   'transfer'          die Umlagerung selbst. Standort des Belegs = ABGEBENDER Standort, Zielstandort = Empfänger.
//                       Vor dem Versand durchläuft sie Vorstufen: Entwurf → Angefordert → Freigegeben. Der Versand
//                       schreibt fest (Nummer, Ware geht in den Transit).
//   'transfer_receipt'  Empfang am Zielstandort, abgeleitet aus der Umlagerung – auch in Teilen.
// Die Oberfläche und der Assistent arbeiten mit den Vorgängen unten (anfordern, freigeben, versenden, empfangen,
// Differenz klären); der Standard-Vertragssatz liefert Liste, Ansicht, Druck und Storno.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { defineDocumentContracts } from '@shared/document'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { zPickBarcode, zPickPart } from './bins'
import { zDay, zLocationRef, zNote, zPerson, zReason, zUnitInfo, zUnitQty, zUnitQtyOrZero } from './common'

export const TRANSFER_TYPE = 'transfer'
export const TRANSFER_RECEIPT_TYPE = 'transfer_receipt'

export const TRANSFER_STATUS = {
  draft: 'draft',
  requested: 'requested',
  approved: 'approved',
  rejected: 'rejected',
  cancelled: 'cancelled',
  inTransit: 'in_transit',
  partiallyReceived: 'partially_received',
  received: 'received',
  closed: 'closed',
  reversed: 'reversed',
} as const

const READ = 'inventory.transfer.read'
const REQUEST = 'inventory.transfer.request'
const SHIP = 'inventory.transfer.ship'
const RECEIVE = 'inventory.transfer.receive'
const APPROVE = 'inventory.transfer.approve'

export const transfer = defineDocumentContracts({
  module: 'inventory',
  entity: 'transfer',
  documentType: TRANSFER_TYPE,
  title: 'Umlagerung',
  titlePlural: 'Umlagerungen',
  permissions: { read: READ, write: REQUEST, post: SHIP, reverse: SHIP },
  financial: false,
  keywords: ['Umlagerung', 'Nachschub', 'Filialbestellung', 'Transit', 'unterwegs', 'Fahrzeug beladen'],
})

export const transferReceipt = defineDocumentContracts({
  module: 'inventory',
  entity: 'transferReceipt',
  documentType: TRANSFER_RECEIPT_TYPE,
  title: 'Umlagerungs-Eingang',
  titlePlural: 'Umlagerungs-Eingänge',
  permissions: { read: READ, write: RECEIVE, post: RECEIVE, reverse: RECEIVE },
  derivesFrom: [TRANSFER_TYPE],
  financial: false,
  keywords: ['Umlagerung empfangen', 'Empfang', 'Eingang'],
})

/** Druckmodell der Umlagerung (Umlagerungsschein A4) */
export const transferPrint = transfer.print
/** Entwurf verwerfen (nur im Zustand „Entwurf“; Angefordertes wird abgebrochen) */
export const transferDiscard = transfer.discard
/** Versand stornieren: holt die Ware aus dem Transit zurück – nur solange nichts empfangen ist */
export const transferReverse = transfer.reverse
/** Empfang stornieren: Die Ware steht danach wieder im Transit */
export const transferReceiptReverse = transferReceipt.reverse

export const zTransferRow = z.object({
  id: zId,
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  documentDate: z.string(),
  deliveryDate: z.string().nullable(),
  from: zLocationRef,
  to: zLocationRef,
  lineCount: z.int(),
  requestedByName: z.string().nullable(),
  /** der Nutzer ist am Zug: freigeben, versenden oder empfangen */
  waitingForMe: z.boolean(),
})

export const transferList = defineContract({
  name: 'inventory.transfer.list',
  title: 'Umlagerungen anzeigen',
  description: 'Listet Umlagerungen, die an einem eigenen Standort beginnen oder enden – mit Zustand und beiden Standorten.',
  llm: {
    description:
      'Sucht und listet Umlagerungen, die an einem Standort des Nutzers beginnen oder enden. Filter: Zustand ' +
      '(draft, requested, approved, in_transit, partially_received, received, closed, rejected, cancelled, reversed), ' +
      '`open` = nur noch nicht abgeschlossene, `locationId` (als abgebender oder empfangender Standort), Zeitraum, ' +
      'Suchtext (Nummer). `waitingForMe` sagt, ob der Nutzer am Zug ist. Verwenden für „Was ist unterwegs nach …?“ ' +
      'und um die Kennung einer Umlagerung zu finden; Einzelheiten mit inventory.transfer.detail.',
    keywords: ['Umlagerungen', 'Liste', 'unterwegs', 'offen', 'Nachschub', 'Transit'],
  },
  input: listInput({
    sortBy: ['documentDate', 'number'],
    defaultSort: { by: 'documentDate', dir: 'desc' },
    filters: {
      status: z.array(z.string().max(40)).max(12).optional(),
      /** nur Umlagerungen, die noch nicht abgeschlossen, abgelehnt, abgebrochen oder storniert sind */
      open: z.boolean().optional(),
      /** als abgebender ODER empfangender Standort */
      locationId: zId.optional(),
      from: zDay.optional(),
      to: zDay.optional(),
    },
  }),
  output: listOutput(zTransferRow),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

// ---- Standorte und Vorschlag -----------------------------------------------------------------------------------

export const transferLocations = defineContract({
  name: 'inventory.transfer.locations',
  title: 'Standorte für Umlagerungen anzeigen',
  description: 'Lager, Filialen und Fahrzeuge des Betriebs – als Quelle oder Ziel einer Umlagerung.',
  llm: {
    description:
      'Liefert alle aktiven Lager, Filialen und Fahrzeuge mit Kürzel und Namen, dazu je Standort das liefernde Lager ' +
      '(`supplyLocationId`) und was der Nutzer dort darf. Verwenden, um aus „Ismaning“ oder „Zentrallager“ die ' +
      'Kennung für eine Umlagerung zu ermitteln.',
    keywords: ['Standort', 'Lager', 'Filiale', 'Fahrzeug', 'Quelle', 'Ziel'],
  },
  input: z.strictObject({}),
  output: z.object({
    locations: z.array(
      zLocationRef.extend({
        supplyLocationId: zId.nullable(),
        /** der Nutzer darf für diesen Standort anfordern bzw. von ihm versenden */
        canRequest: z.boolean(),
        canShip: z.boolean(),
        canReceive: z.boolean(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const zProposalLine = z.object({
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  unit: zUnitInfo,
  baseUnit: z.string(),
  /** alle Mengen in Tausendstel der Basiseinheit */
  available: z.int(),
  inTransit: z.int(),
  reorderQty: z.int(),
  targetQty: z.int(),
  /** frei verfügbar am abgebenden Standort */
  sourceAvailable: z.int(),
  /** Bedarf bis zum Zielbestand */
  neededQty: z.int(),
  /** Vorschlag im Gebinde `unit` in Tausendstel (ganze Gebinde, höchstens was die Quelle frei hat) */
  proposedQty: z.int(),
  /** true: Die Quelle hat weniger frei, als gebraucht wird */
  short: z.boolean(),
})

export const transferPropose = defineContract({
  name: 'inventory.transfer.propose',
  title: 'Nachschub vorschlagen',
  description: 'Schlägt vor, was ein Standort vom liefernden Lager braucht: alles unter Meldebestand, aufgefüllt bis zum Zielbestand.',
  llm: {
    description:
      'Rechnet den Nachschub für einen Standort: jeder Artikel, dessen verfügbarer Bestand plus Unterwegs-Menge unter ' +
      'dem Meldebestand liegt, wird bis zum Zielbestand aufgefüllt – in ganzen Lagergebinden und höchstens so viel, ' +
      'wie der abgebende Standort frei verfügbar hat. `proposedQty` im Gebinde in Tausendstel (1 Karton = 1000), die ' +
      'übrigen Mengen in Tausendstel der Basiseinheit. Legt nichts an – danach inventory.transfer.request mit den ' +
      'gewünschten Zeilen.',
    keywords: ['Nachschub', 'Vorschlag', 'Meldebestand', 'Zielbestand', 'auffüllen', 'Filiale', 'was fehlt'],
  },
  input: z.strictObject({ toLocationId: zId, fromLocationId: zId.optional() }),
  output: z.object({ from: zLocationRef, to: zLocationRef, lines: z.array(zProposalLine) }),
  permission: REQUEST,
  risk: 'read',
  scope: (input) => [input.toLocationId],
})

// ---- Vorgang ---------------------------------------------------------------------------------------------------

const zStep = z.object({
  key: z.enum(['requested', 'approved', 'shipped', 'received', 'closed']),
  label: z.string(),
  state: z.enum(['done', 'current', 'open', 'skipped']),
  at: z.date().nullable(),
  byName: z.string().nullable(),
})

export const zTransferLine = z.object({
  id: zId,
  pos: z.int(),
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  /** Gebinde der Zeile – alle Mengen der Zeile stehen in Tausendstel dieses Gebindes */
  unit: zUnitInfo,
  baseUnit: z.string(),
  /** ursprünglich angefordert */
  requestedQty: z.int(),
  /** zu versenden bzw. versandt */
  qty: z.int(),
  receivedQty: z.int(),
  /** davon beim Empfang als beschädigt gemeldet */
  damagedQty: z.int(),
  /** noch unterwegs (versandt − empfangen − geklärt) */
  inTransitQty: z.int(),
  /** als Fehlmenge ausgebucht oder an die Quelle zurückgebucht */
  settledQty: z.int(),
  /** frei verfügbar am abgebenden Standort (Basiseinheit) – nur vor dem Versand und nur für die abgebende Seite */
  sourceAvailable: z.int().nullable(),
  lots: z.array(z.object({ lotId: zId.nullable(), lotNo: z.string().nullable(), bestBefore: z.string().nullable(), qty: z.int() })),
  note: z.string().nullable(),
})

export const zTransferDetail = z.object({
  id: zId,
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  posted: z.boolean(),
  version: z.int(),
  from: zLocationRef,
  to: zLocationRef,
  documentDate: z.string(),
  /** Wunschtermin */
  deliveryDate: z.string().nullable(),
  notes: z.string().nullable(),
  /** Grund einer Ablehnung, eines Abbruchs oder Stornos */
  reason: z.string().nullable(),
  requestedBy: zPerson.nullable(),
  steps: z.array(zStep),
  lines: z.array(zTransferLine),
  receipts: z.array(
    z.object({
      id: zId,
      number: z.string().nullable(),
      status: z.string(),
      statusLabel: z.string(),
      documentDate: z.string(),
      at: z.date().nullable(),
      byName: z.string().nullable(),
      reversed: z.boolean(),
      lines: z.array(z.object({ lineId: zId, qty: z.int(), damagedQty: z.int() })),
    }),
  ),
  /** Korrekturbuchungen aus beschädigter Ware */
  adjustments: z.array(z.object({ id: zId, number: z.string().nullable(), status: z.string(), statusLabel: z.string() })),
  /** Stornobeleg des Versands */
  reversal: z.object({ id: zId, number: z.string().nullable() }).nullable(),
  /** Wert der Ware im Transit – nur mit „Einstandspreise sehen“ */
  transitValueCents: z.int().optional(),
  /** was der Nutzer mit dieser Umlagerung jetzt tun darf */
  allowed: z.object({
    edit: z.boolean(),
    submit: z.boolean(),
    approve: z.boolean(),
    reject: z.boolean(),
    ship: z.boolean(),
    receive: z.boolean(),
    settle: z.boolean(),
    cancel: z.boolean(),
    reverse: z.boolean(),
    discard: z.boolean(),
  }),
  /** ein Satz: wer jetzt am Zug ist */
  hint: z.string(),
})
export type TransferDetail = z.output<typeof zTransferDetail>

export const transferDetail = defineContract({
  name: 'inventory.transfer.detail',
  title: 'Umlagerung anzeigen',
  description: 'Eine Umlagerung mit Stand je Schritt, Mengen je Position (angefordert, versandt, empfangen, unterwegs) und Empfängen.',
  llm: {
    description:
      'Liefert eine Umlagerung: abgebender und empfangender Standort, Zustand, Schritte mit Person und Zeit, je ' +
      'Position angeforderte, versandte, empfangene, beschädigte und noch im Transit stehende Menge (im Gebinde der ' +
      'Zeile in Tausendstel), die Empfänge und in `allowed`, was der Nutzer jetzt tun darf. Verwenden, bevor ' +
      'freigegeben, versandt, empfangen oder eine Differenz geklärt wird.',
    keywords: ['Umlagerung', 'Stand', 'unterwegs', 'Transit', 'Differenz', 'empfangen'],
  },
  input: z.strictObject({ id: zId }),
  output: zTransferDetail,
  permission: READ,
  risk: 'read',
  scope: 'record',
  fieldPermissions: { transitValueCents: PERMISSION_COSTS_READ },
})

const zRequestLine = z.strictObject({
  itemId: zId,
  /** Gebinde (item_unit.id); ohne Angabe das Lagergebinde */
  unitId: zId.nullish(),
  /** Menge im Gebinde in Tausendstel (1 Kasten = 1000) */
  qty: zUnitQty,
  note: zNote,
})

export const transferRequest = defineContract({
  name: 'inventory.transfer.request',
  title: 'Umlagerung anfordern',
  description: 'Legt eine Umlagerung an oder ändert sie und sendet sie an den abgebenden Standort.',
  llm: {
    description:
      'Legt eine Umlagerung an (ohne `id`) oder ersetzt Kopf und Zeilen einer noch nicht versandten (mit `id`). ' +
      '`fromLocationId` gibt ab, `toLocationId` empfängt – auch ein Fahrzeug (Tourbeladung). Mengen im Gebinde in ' +
      'Tausendstel (1 Karton = 1000). Mit `submit: true` wird sie angefordert; wer am abgebenden Standort freigeben ' +
      'darf, gibt sie damit zugleich frei (der Bestand wird reserviert). Ohne `submit` bleibt sie ein Entwurf. ' +
      'Braucht das Recht am empfangenden ODER am abgebenden Standort. Zeilen für den Nachschub liefert ' +
      'inventory.transfer.propose. Bucht keinen Bestand – das tut erst inventory.transfer.ship.',
    keywords: ['Umlagerung anlegen', 'Nachschub anfordern', 'Filialbestellung', 'Ware anfordern', 'Fahrzeug beladen', 'umlagern'],
  },
  input: z.strictObject({
    id: zId.optional(),
    version: z.int().optional(),
    fromLocationId: zId,
    toLocationId: zId,
    deliveryDate: zDay.nullish(),
    notes: z.string().trim().max(4000).nullish(),
    lines: z.array(zRequestLine).min(1, { error: 'Bitte erfassen Sie mindestens eine Position.' }).max(500),
    submit: z.boolean().default(true),
  }),
  output: zTransferDetail,
  permission: REQUEST,
  risk: 'write',
  scope: 'record',
  effects: { reversible: true },
  limits: { maxItems: 500, itemCount: (input) => input.lines.length },
  fieldPermissions: { transitValueCents: PERMISSION_COSTS_READ },
})

export const transferApprove = defineContract({
  name: 'inventory.transfer.approve',
  title: 'Umlagerung freigeben',
  description: 'Gibt eine angeforderte Umlagerung frei – ganz oder mit gekürzten Mengen – und reserviert die Ware am abgebenden Standort.',
  llm: {
    description:
      'Gibt eine angeforderte Umlagerung frei. Optional `lines` mit gekürzten Mengen je Position (im Gebinde in ' +
      'Tausendstel; 0 streicht die Position). Die freigegebene Menge wird am abgebenden Standort reserviert, bis ' +
      'versandt wird; reicht der freie Bestand nicht, wird nichts freigegeben. Braucht das Recht am ABGEBENDEN ' +
      'Standort. Zum Ablehnen inventory.transfer.reject.',
    keywords: ['Umlagerung freigeben', 'genehmigen', 'Anforderung freigeben', 'kürzen'],
  },
  input: z.strictObject({
    id: zId,
    lines: z.array(z.strictObject({ lineId: zId, qty: zUnitQtyOrZero })).max(500).optional(),
    note: zNote,
  }),
  output: zTransferDetail,
  permission: APPROVE,
  risk: 'write',
  scope: 'record',
  effects: { reversible: true },
  fieldPermissions: { transitValueCents: PERMISSION_COSTS_READ },
})

export const transferReject = defineContract({
  name: 'inventory.transfer.reject',
  title: 'Umlagerung ablehnen',
  description: 'Lehnt eine angeforderte oder freigegebene Umlagerung mit Grund ab; eine Reservierung wird gelöst.',
  llm: {
    description:
      'Lehnt eine Umlagerung ab, die noch nicht versandt ist, mit Grund. Die Reservierung am abgebenden Standort wird ' +
      'gelöst, die anfordernde Person benachrichtigt. Braucht das Recht am abgebenden Standort.',
    keywords: ['Umlagerung ablehnen', 'Anforderung ablehnen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zTransferDetail,
  permission: APPROVE,
  risk: 'write',
  scope: 'record',
  fieldPermissions: { transitValueCents: PERMISSION_COSTS_READ },
})

export const transferCancel = defineContract({
  name: 'inventory.transfer.cancel',
  title: 'Umlagerung abbrechen',
  description: 'Bricht eine Umlagerung ab, die noch nicht versandt ist; eine Reservierung wird gelöst.',
  llm: {
    description:
      'Bricht eine eigene, noch nicht versandte Umlagerung mit Grund ab (Entwurf, angefordert oder freigegeben). ' +
      'Nach dem Versand geht das nicht mehr – dann den Versand stornieren (inventory.transfer.reverse), solange ' +
      'nichts empfangen ist.',
    keywords: ['Umlagerung abbrechen', 'Anforderung zurückziehen', 'stornieren'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zTransferDetail,
  permission: REQUEST,
  risk: 'write',
  scope: 'record',
  fieldPermissions: { transitValueCents: PERMISSION_COSTS_READ },
})

export const zTransferPickLine = z.object({
  lineId: zId,
  pos: z.int(),
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  unit: zUnitInfo,
  /** freigegebene, zu versendende Menge im Gebinde der Zeile (Tausendstel) */
  qty: z.int(),
  /** davon jetzt greifbar (Reservierung dieser Umlagerung + frei, ohne gesperrte Plätze und Chargen) */
  pickableQty: z.int(),
  lotTracked: z.boolean(),
  /** Vorschlag nach FEFO, in der Reihenfolge des Laufwegs */
  proposal: z.array(zPickPart),
  barcodes: z.array(zPickBarcode),
  note: z.string().nullable(),
})

export const transferPicking = defineContract({
  name: 'inventory.transfer.picking',
  title: 'Kommissionierliste einer Umlagerung',
  description: 'Die zu versendenden Positionen einer freigegebenen Umlagerung mit Vorschlag für Lagerplatz und Warenposten – in der Reihenfolge des Laufwegs.',
  llm: {
    description:
      'Liefert die Kommissionierliste einer noch nicht versandten Umlagerung: je Position die freigegebene Menge im ' +
      'Gebinde (Tausendstel), was davon greifbar ist und den Vorschlag (Platz, Warenposten, Ablaufdatum, Menge) nach FEFO, sortiert ' +
      'nach dem Laufweg. Ändert nichts – gebucht wird mit inventory.transfer.ship (`picks` je Position).',
    keywords: ['Kommissionierliste', 'Umlagerung packen', 'Nachschub kommissionieren', 'Pickliste', 'Lagerplatz'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    number: z.string().nullable(),
    status: z.string(),
    statusLabel: z.string(),
    from: zLocationRef,
    to: zLocationRef,
    deliveryDate: z.string().nullable(),
    /** Funktion „Lagerplätze“ an: Plätze zeigen, nach Laufweg sortieren */
    bins: z.boolean(),
    lines: z.array(zTransferPickLine),
    today: z.string(),
  }),
  permission: SHIP,
  risk: 'read',
  scope: 'record',
})

/** Ein bestätigter Griff beim Kommissionieren: Menge (Tausendstel des Gebindes der Zeile), Platz, Charge, Grund. */
export const zTransferPick = z.strictObject({
  qty: zUnitQty,
  /** Kürzel des Lagerplatzes (nur mit der Funktion „Lagerplätze“) */
  binCode: z.string().trim().min(1).max(40).nullish(),
  lotNo: z.string().trim().max(80).nullish(),
  /** Grund, wenn eine andere Charge als vorgeschlagen (mit späterem MHD) gegriffen wurde */
  lotReason: zNote,
})

export const transferShip = defineContract({
  name: 'inventory.transfer.ship',
  title: 'Umlagerung versenden',
  description: 'Bucht den Versand: Die kommissionierte Ware verlässt den abgebenden Standort und steht im Transit.',
  llm: {
    description:
      'Bucht den Versand einer freigegebenen Umlagerung: Die Ware geht vom abgebenden Standort in den Transit ' +
      '(Gesperrtes nie), die Umlagerung erhält ihre Nummer. `lines` nennt die tatsächlich ' +
      'kommissionierte Menge je Position im Gebinde in Tausendstel (weniger als freigegeben ist erlaubt, 0 streicht ' +
      'die Position); ohne Angabe gilt die freigegebene Menge. Wer freigeben darf, kann auch eine angeforderte ' +
      'Umlagerung direkt versenden. Reicht der Bestand nicht, wird nichts gebucht. Rückgängig nur per Storno, ' +
      'solange nichts empfangen ist. `picks` nennt, woher die Menge einer Position kam (Platz-Kürzel, Warenposten; Summe = ' +
      '`qty`) – ein anderer Warenposten als vorgeschlagen, der später abläuft, braucht `lotReason`; ohne `picks` teilt der Kern ' +
      'nach FEFO zu (inventory.transfer.picking zeigt den Vorschlag).',
    keywords: ['Versand buchen', 'versenden', 'kommissionieren', 'verladen', 'Fahrzeug beladen', 'Transit'],
  },
  input: z.strictObject({
    id: zId,
    lines: z
      .array(z.strictObject({ lineId: zId, qty: zUnitQtyOrZero, lotId: zId.nullish(), picks: z.array(zTransferPick).max(50).optional() }))
      .max(500)
      .optional(),
    documentDate: zDay.optional(),
  }),
  output: zTransferDetail,
  permission: SHIP,
  risk: 'financial',
  scope: 'record',
  effects: { reversible: true },
  fieldPermissions: { transitValueCents: PERMISSION_COSTS_READ },
})

export const transferReceive = defineContract({
  name: 'inventory.transfer.receive',
  title: 'Umlagerung empfangen',
  description: 'Bucht den Empfang am Zielstandort – auch in Teilen. Fehlmengen bleiben im Transit, Beschädigtes geht in eine Korrekturbuchung.',
  llm: {
    description:
      'Bucht den Empfang einer versandten Umlagerung am Zielstandort. `lines` nennt je Position die ANGEKOMMENE ' +
      'Menge im Gebinde in Tausendstel (1 Karton = 1000) und optional `damagedQty` (davon beschädigt). Ohne `lines` ' +
      'gilt alles als vollständig angekommen. Was fehlt, verschwindet nicht: Es bleibt im Transit stehen, bis der ' +
      'abgebende Standort die Differenz klärt (inventory.transfer.settle). Für Beschädigtes entsteht eine ' +
      'Korrekturbuchung mit Grund „Bruch“ am Zielstandort – über der Grenze der Rolle wartet sie auf Freigabe. ' +
      'Mehr als unterwegs ist lässt sich nicht empfangen.',
    keywords: ['Empfang buchen', 'Umlagerung empfangen', 'annehmen', 'Fehlmenge', 'beschädigt', 'Bruch', 'angekommen'],
  },
  input: z.strictObject({
    id: zId,
    lines: z
      .array(z.strictObject({ lineId: zId, qty: zUnitQtyOrZero, damagedQty: zUnitQtyOrZero.optional(), note: zNote }))
      .max(500)
      .optional(),
    documentDate: zDay.optional(),
  }),
  output: zTransferDetail,
  permission: RECEIVE,
  risk: 'financial',
  scope: 'record',
  effects: { reversible: true },
  fieldPermissions: { transitValueCents: PERMISSION_COSTS_READ },
})

export const TRANSFER_RESOLUTIONS = ['write_off', 'return'] as const

export const transferSettle = defineContract({
  name: 'inventory.transfer.settle',
  title: 'Transitdifferenz klären',
  description: 'Klärt, was von einer Umlagerung noch im Transit steht: als Fehlmenge ausbuchen oder an den abgebenden Standort zurückbuchen.',
  llm: {
    description:
      'Klärt die offene Transitmenge einer Umlagerung, nachdem der Empfänger weniger gemeldet hat als versandt wurde. ' +
      '`resolution: "return"` bucht den Rest an den abgebenden Standort zurück (die Ware wurde nachgezählt und ist ' +
      'noch da), `"write_off"` bucht ihn mit Grund als Verlust aus (Bruch, Schwund – `reasonCodeId` eines Grunds der ' +
      'Art Korrektur ist dann Pflicht). Danach ist die Umlagerung abgeschlossen. Braucht das Recht am abgebenden ' +
      'Standort; eine Ausbuchung zählt gegen die Wertgrenze für Korrekturfreigaben. Kommt die Ware doch noch an: ' +
      'stattdessen inventory.transfer.receive.',
    keywords: ['Differenz klären', 'Transitdifferenz', 'Fehlmenge', 'ausbuchen', 'zurückbuchen', 'nachgezählt'],
  },
  input: z.strictObject({
    id: zId,
    resolution: z.enum(TRANSFER_RESOLUTIONS),
    reasonCodeId: zId.nullish(),
    note: zReason,
  }),
  output: zTransferDetail,
  permission: APPROVE,
  risk: 'financial',
  scope: 'record',
  fieldPermissions: { transitValueCents: PERMISSION_COSTS_READ },
})
